import { useEffect, useRef, useState } from 'react';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import './BulkSetTeamModal.css';

type Props = {
  selectedCount: number;
  teamNames: string[];
  submitting: boolean;
  onClose: () => void;
  /**
   * Called when the user confirms. `teamName` is the trimmed input, or null if the user
   * cleared the field (explicit "clear team" path).
   * `carryForward`, when true, means the caller should also apply the team name to every
   * subsequent un-named page — carry-forward logic lives in the parent so it can compute
   * the effective set from the full batch state.
   */
  onSubmit: (teamName: string | null, carryForward: boolean) => void;
};

const DATALIST_ID = 'bulk-set-team-modal-datalist';

export const BulkSetTeamModal = ({
  selectedCount,
  teamNames,
  submitting,
  onClose,
  onSubmit,
}: Props) => {
  const [teamName, setTeamName] = useState('');
  const [carryForward, setCarryForward] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on open; Esc closes
  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = teamName.trim();
    onSubmit(trimmed.length > 0 ? trimmed : null, carryForward);
  };

  const handleClearTeam = () => {
    if (submitting) return;
    // Explicit "clear team assignment" path
    onSubmit(null, carryForward);
  };

  return (
    <div className="bulk-set-team-modal__overlay" onClick={onClose}>
      <div
        className="bulk-set-team-modal"
        role="dialog"
        aria-labelledby="bulk-set-team-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bulk-set-team-modal__header">
          <h3 id="bulk-set-team-modal-title" className="bulk-set-team-modal__title">
            Set team for {selectedCount} {selectedCount === 1 ? 'page' : 'pages'}
          </h3>
          <ModalCloseButton onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="bulk-set-team-modal__form">
          <label className="bulk-set-team-modal__field">
            <span className="bulk-set-team-modal__label">Team name</span>
            <input
              ref={inputRef}
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              list={DATALIST_ID}
              placeholder="e.g. East Cobb Astros 16U"
              className="bulk-set-team-modal__input"
              disabled={submitting}
              autoComplete="off"
            />
            <datalist id={DATALIST_ID}>
              {teamNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>

          <span className="bulk-set-team-modal__carry-forward" id="bulk-set-team-modal-setcarryforward-e-target-checked-d-0">
            <input
              type="checkbox"
              checked={carryForward}
              onChange={(e) => setCarryForward(e.target.checked)}
              disabled={submitting}
            />
            <span>Apply to all following pages that do not have a team yet</span>
          </span>

          <div className="bulk-set-team-modal__actions" role="group" aria-labelledby="bulk-set-team-modal-setcarryforward-e-target-checked-d-0">
            <button
              type="button"
              className="bulk-set-team-modal__button bulk-set-team-modal__button--danger"
              onClick={handleClearTeam}
              disabled={submitting}
              title="Remove team assignment from selected pages"
            >
              Clear team
            </button>
            <div className="bulk-set-team-modal__actions-right">
              <button
                type="button"
                className="bulk-set-team-modal__button"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bulk-set-team-modal__button bulk-set-team-modal__button--primary"
                disabled={submitting || teamName.trim().length === 0}
              >
                {submitting ? 'Saving…' : 'Set Team'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
